/**
 * Resolve the initial auth state on app boot.
 *
 * Why this is needed: authSlice starts at status 'idle', and RequireAuth treats
 * 'idle' as "not settled yet" and shows a spinner rather than the sign-in
 * screen (so a reload doesn't flash it). Nothing moved the status off 'idle' on
 * a cold load, so the guard spun forever and every protected route was
 * unreachable.
 *
 * This is what makes the landing page land in the right place: a visitor whose
 * cookies hold a full session is authenticated here, before the first render,
 * and goes straight to the app; anyone else is anonymous and is shown the
 * sign-in screen at the landing URL.
 *
 * A session comes from a sign-in — the local form, or Common Services' SSO,
 * which writes the shared *.amd.com cookies — and is restored here from the
 * legacy cookie set, token and identity together, so F5 does not sign you out.
 *
 * Roles are re-resolved here rather than restored verbatim, because the mirror
 * carries the server's full { CCA, EIA } matrix and only the branch for the
 * product currently being served may decide what this session can do. Restoring
 * a stored role list would carry CCA's role into an EIA tab.
 *
 * When the backend exposes a refresh endpoint this becomes the place to await
 * it, then dispatch authenticated()/signedOut() on the result — and the cookie
 * mirror can be deleted entirely. Leave the status at 'idle' while awaiting it.
 * Do NOT dispatch authenticating() for this: RequireAuth deliberately keeps the
 * sign-in screen up during 'authenticating' (it is the local form's own
 * in-flight state), so a boot-time refresh would flash the sign-in screen at
 * every reload instead of showing the spinner.
 */
import { resolveRoles } from '@features/auth';
import { getVariant, resolveAppMode } from '@variant/appMode';

import { authenticated, signedOut } from './authSlice';
import { setCookie } from './cookies';
import { restoreSession } from './tokenStore';

export function bootstrapAuth(store) {
  const mode = resolveAppMode();
  const restored = restoreSession();

  if (restored?.user) {
    const { roles, features } = resolveRoles(restored.featuresData, mode);
    store.dispatch(authenticated({ user: restored.user, roles, features }));
  } else {
    store.dispatch(signedOut());
  }

  /*
   * Legacy sets `application` on every app load, not only at login
   * (App.vue:93), so other AMD pages can tell which product the user came from.
   * Reproduced for parity, including the fact that it is written while
   * anonymous.
   *
   * It MUST come after the dispatch above. `signedOut` runs the sign-out
   * listener, which clears the whole cookie set — writing this first means the
   * anonymous branch deletes it again a tick later, and the cookie never
   * survives a cold load.
   *
   * shortName, not mode.toUpperCase(): variants.config.js is the single source
   * of truth for what each product is called on the wire.
   */
  setCookie('application', getVariant(mode).shortName);
}
